import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getMissingCompanyFields } from '../utils/companyData';
import { scrollToTop } from '../utils/scrollToTop';
import { API_BASE } from '../config';
import EmailPillField from '../components/contract/EmailPillField';
import { useConfirm } from '../components/ui/Modal';
import { useTranslation } from '../translations';
import { IconButton, slideIn, panelTopHairline } from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import SimpleSelect from '../ui/SimpleSelect';
import { TylkoOdczytWPrzerwie, useKontoWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// Zmienne emaila faktury: kod → KLUCZ i18n (pigułka pokazuje nazwę, magazyn trzyma {{kod}}).
// P8.1e: etykieta jest wyłącznie prezentacją — klik wstawia KOD przez insertInvoiceVar(name),
// więc przetłumaczenie jej nie rusza treści szablonu maila.
const INVOICE_VAR_KEYS = {
  invoiceNumber: 'contractSettings.varInvoiceNumber',
  docLabel: 'contractSettings.varDocLabel',
  totalGross: 'contractSettings.varTotalGross',
  // 12.10.2026 (test odbiorczy płatności): wpłaty z rejestru trafiają na fakturę, więc mail może
  // podać, ile zapłacono i ile zostało (lib/invoiceDocument.js#zmienneMailaFaktury).
  amountPaid: 'contractSettings.varAmountPaid',
  amountDue: 'contractSettings.varAmountDue',
  organizationName: 'contractSettings.varOrganizationName',
  buyerName: 'contractSettings.varBuyerName',
};

// ── Ikony SVG ──────────────────────────────────────────────────────────────
const SaveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="16" x2="12" y2="12"/>
    <line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);
const BuildingIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
    <line x1="9" y1="5" x2="9" y2="9"/>
    <line x1="15" y1="5" x2="15" y2="9"/>
  </svg>
);

// ── Styled Components ──────────────────────────────────────────────────────
const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 24px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  min-height: calc(100vh - 48px);
  /* Reszta panelu (PanelContainer, ui/common.js) dostaje przy wejsciu na strone
     delikatny fade+wjazd z dolu. Ta strona miala WLASNY Container bez tej animacji. */
  animation: ${slideIn} 0.6s ease-out;
  ${panelTopHairline}
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 32px;
  padding-top: 8px;
`;

const HeaderCenter = styled.div`
  flex: 1;
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 2rem;
  font-weight: 800;
  color: var(--text);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;
const TitleIcon = styled.div`
  color: var(--primary);
  display: flex;
`;
const Subtitle = styled.p`
  margin: 0;
  font-size: 1rem;
  color: var(--text-muted);
`;

const Card = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px var(--shadow-color);
`;
const CardTitle = styled.h2`
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--input-border);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => props.$full && 'grid-column: 1 / -1;'}
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;
const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--input-border);
  border-radius: 10px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;
const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const PrimaryButton = styled(Button)`
  background: var(--primary);
  color: var(--primary-contrast);
  box-shadow: var(--shadow-primary);
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: var(--shadow-md); }
`;
const DangerButton = styled(Button)`
  background: var(--danger);
  color: var(--primary-contrast);
  &:hover:not(:disabled) { filter: brightness(0.95); }
`;

const Alert = styled.div`
  padding: 14px 18px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  ${props => props.type === 'success' && `background:var(--success-bg);border:1px solid var(--success-border);color:var(--success);`}
  ${props => props.type === 'error'   && `background:var(--danger-bg);border:1px solid var(--danger-border);color:var(--danger);`}
  ${props => props.type === 'info'    && `background:var(--info-bg);border:1px solid var(--info-border);color:var(--info);`}
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;
const Tag = styled.code`
  background: var(--input-border);
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: var(--text-muted);
`;

const HintChip = styled.button`
  border: 1px solid var(--input-border);
  background: var(--input-bg);
  color: var(--text);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  text-align: left;
  &:hover { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const FieldHint = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--text-muted);
`;
const FieldError = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--danger);
`;

// ── Komponent główny ────────────────────────────────────────────────────────
const EMPTY = {
  company_name: '',
  company_address: '',
  company_nip: '',
  company_regon: '',
  company_krs: '',
  company_street: '',
  company_postal_code: '',
  company_city: '',
  company_phone: '',
  company_email: '',
  company_bank_account: '',
  company_bank_name: '',
  representative_name: '',
  representative_title: '',
  contract_extra_clauses: '',
  company_logo: '',
  company_logo_align: 'center',
  company_logo_height_px: 70,
  offer_title: '',
};

// Zapis wysyła WYŁĄCZNIE pola tego ekranu. `form` powstaje z całego wiersza ustawień, więc
// niósł też szablon oferty z chwili otwarcia karty: stara karta „Danych firmy” cofała po zapisie
// opublikowany szablon oferty i jego szkic (test odbiorczy 11.10.2026). Serwer od tej zmiany
// zapisuje tylko pola obecne w payloadzie. `offer_title` należy do konstruktora oferty.
// Z `wczytane` (stan z serwera) idą tylko pola ZMIENIONE na tym ekranie: karta otwarta rano nie
// cofa po zapisie pól, które ktoś zmienił w międzyczasie w innej karcie (test odbiorczy 11.10.2026).
const wartoscPola = (v) => (v === undefined || v === null ? '' : String(v));
export function polaDanychFirmy(form, wczytane) {
  return Object.fromEntries(
    Object.keys(EMPTY)
      .filter((k) => k !== 'offer_title')
      .filter((k) => !wczytane || wartoscPola(form[k]) !== wartoscPola(wczytane[k]))
      .map((k) => [k, form[k]])
  );
}

// Wysokość logo: liczba całkowita 40-140 px, czyli zakres, który stosuje dokument (umowa
// i podgląd); puste pole = 70 px. Lustro walidacji PUT /api/contract-settings. Dawniej pole
// zamieniało każdą nie-liczbę na 70, więc nie dało się go wyczyścić, a serwer po cichu zapisywał
// 70 zamiast wartości spoza 20-200 (test odbiorczy 11.10.2026).
export const LOGO_WYSOKOSC = { min: 40, max: 140, domyslna: 70 };
export function wysokoscLogoZPola(surowa) {
  const tekst = wartoscPola(surowa).trim();
  if (!tekst) return { wartosc: LOGO_WYSOKOSC.domyslna, blad: false };
  const liczba = /^\d+$/.test(tekst) ? Number(tekst) : NaN;
  const blad = !Number.isInteger(liczba) || liczba < LOGO_WYSOKOSC.min || liczba > LOGO_WYSOKOSC.max;
  return { wartosc: blad ? null : liczba, blad };
}

// Stawka domyślna jako KOD tekstowy ('23', '8', '5', '0'): lista ma wartości tekstowe, a liczba
// w polu tekstowym dawała po wyczyszczeniu 0% (`Number('') || 0`). Status 'active' obejmuje też
// NULL z serwera („nie wybrano”, działa jak czynny podatnik).
const EMPTY_INV = {
  vat_status: 'active',
  vat_exemption_basis: '',
  default_vat_rate: '23',
  default_payment_method: 'transfer',
  default_payment_term_days: 7,
  invoice_prefix_vat: 'FV',
  invoice_prefix_proforma: 'PRO',
  invoice_prefix_simplified: 'PAR',
  invoice_prefix_correction: 'KOR',
  invoice_auto_email: true,
  invoice_email_subject: '',
  invoice_email_body: '',
};

export const STAWKI_DOMYSLNE = ['23', '8', '5', '0'];

// Podpowiedzi podstawy zwolnienia: tekst trafia na fakturę i do P_19A w KSeF, więc zostaje po
// polsku niezależnie od języka panelu (dane, nie UI). „…” w art. 43 znaczy „wpisz punkt”; zapis
// z nim jest odrzucany (lustro lib/vatRate.js#sprawdzPodstawe).
export const PODSTAWY_ZWOLNIENIA = [
  { wartosc: 'art. 113 ust. 1 ustawy o VAT', etykieta: 'contractSettings.vatBasis113' },
  { wartosc: 'art. 113 ust. 9 ustawy o VAT', etykieta: 'contractSettings.vatBasis1139' },
  { wartosc: 'art. 43 ust. 1 pkt … ustawy o VAT', etykieta: 'contractSettings.vatBasis43' },
];

/** Błąd podstawy zwolnienia przed zapisem (klucz tłumaczenia) albo null. */
export function bladPodstawy(invForm) {
  if (invForm.vat_status !== 'exempt') return null;
  const tekst = wartoscPola(invForm.vat_exemption_basis).trim();
  if (!tekst) return 'contractSettings.vatBasisRequired';
  if (/…|\.\.\./.test(tekst)) return 'contractSettings.vatBasisIncomplete';
  return null;
}

// Ustawienia faktur idą tak jak Dane firmy: tylko pola zmienione na tym ekranie, bo serwer
// zapisuje wyłącznie klucze obecne w payloadzie (F.14). Liczby w formacie, którego oczekuje API.
export function polaUstawienFaktur(form, wczytane) {
  return Object.fromEntries(
    Object.keys(EMPTY_INV)
      .filter((k) => !wczytane || wartoscPola(form[k]) !== wartoscPola(wczytane[k]))
      .map((k) => {
        if (k === 'default_vat_rate') return [k, Number(form[k])];
        if (k === 'default_payment_term_days') return [k, parseInt(form[k], 10) || 0];
        if (k === 'vat_exemption_basis') return [k, wartoscPola(form[k]).trim()];
        return [k, form[k]];
      })
  );
}

// P8.1e: kod → KLUCZ i18n. <option value={k}> niesie KOD, etykieta to tylko tekst opcji.
const PAYMENT_METHOD_KEYS = {
  transfer: 'contractSettings.payTransfer',
  cash: 'contractSettings.payCash',
  card: 'contractSettings.payCard',
  blik: 'contractSettings.payBlik',
  other: 'contractSettings.payOther',
};

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  input { display: none; }
  .track {
    width: 40px; height: 22px; border-radius: 999px;
    background: ${p => p.checked ? 'var(--primary)' : 'var(--input-border)'};
    position: relative; transition: background .2s;
  }
  .track::after {
    content: ''; position: absolute; top: 2px; left: ${p => p.checked ? '20px' : '2px'};
    width: 18px; height: 18px; border-radius: 50%; background: #fff; /* gałka przełącznika — zawsze biała, jak w EmailSettings.js/Notifications.js */
    transition: left .2s; box-shadow: var(--shadow-sm);
  }
`;

export default function ContractSettings() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t } = useTranslation();
  // F.24: w przerwie dane firmy są tylko do podglądu (PUT /api/contract-settings odrzuca bramka),
  // a ustawienia faktur zapisują się normalnie, bo faktury i KSeF działają w przerwie.
  const { wPrzerwie, powod: powodOdczytu } = useKontoWPrzerwie();
  // Musi żyć w komponencie, bo rozwija klucz przez t(); nieznana zmienna zostaje jako {{kod}}.
  const invoiceLabelFor = (name) => (INVOICE_VAR_KEYS[name] ? t(INVOICE_VAR_KEYS[name]) : `{{${name}}}`);
  const [form, setForm]       = useState(EMPTY);
  const [savedForm, setSavedForm] = useState(EMPTY);
  const [invForm, setInvForm] = useState(EMPTY_INV);
  const [savedInvForm, setSavedInvForm] = useState(EMPTY_INV);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [alert, setAlert]     = useState(null); // { type, msg }
  // Odczyt ustawień się nie udał: formularz zostaje pusty, więc zapis wysłałby puste dane firmy
  // (i domyślne ustawienia faktur) w miejsce zapisanych. Zapis jest wtedy wyłączony, jak
  // w konstruktorach umowy i oferty (test odbiorczy 11.10.2026).
  const [loadFailed, setLoadFailed] = useState(false);
  const [logoHeightTouched, setLogoHeightTouched] = useState(false);
  const invDirty = JSON.stringify(invForm) !== JSON.stringify(savedInvForm);
  const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm) || invDirty;
  const logoHeightChanged = wartoscPola(form.company_logo_height_px) !== wartoscPola(savedForm.company_logo_height_px);
  const logoHeightError = logoHeightChanged && wysokoscLogoZPola(form.company_logo_height_px).blad;

  // ── Ładowanie ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, invRes] = await Promise.all([
          fetch(`${API_BASE}/api/contract-settings`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/invoice-settings`, { credentials: 'include' })
        ]);

        if (!settingsRes.ok) throw new Error(`HTTP ${settingsRes.status}`);

        const settingsData = await settingsRes.json();
        if (settingsData.settings) {
          const normalized = { ...EMPTY, ...settingsData.settings };
          setForm(normalized);
          setSavedForm(normalized);
        } else {
          setForm(EMPTY);
          setSavedForm(EMPTY);
        }

        // Nieudany odczyt ustawień faktur też blokuje zapis: zmienione pola liczone są względem
        // stanu z serwera, a bez niego zapis wysłałby domyślne prefiksy i stawkę VAT.
        if (!invRes.ok) throw new Error(`HTTP ${invRes.status}`);
        const invData = await invRes.json();
        if (invData.settings) {
          const normalizedInv = {
            ...EMPTY_INV,
            ...invData.settings,
            vat_status: invData.settings.vat_status === 'exempt' ? 'exempt' : 'active',
            vat_exemption_basis: invData.settings.vat_exemption_basis || '',
            default_vat_rate: invData.settings.default_vat_rate != null ? String(Number(invData.settings.default_vat_rate)) : '23',
            default_payment_term_days: invData.settings.default_payment_term_days != null ? Number(invData.settings.default_payment_term_days) : 7,
            invoice_auto_email: invData.settings.invoice_auto_email !== false
          };
          setInvForm(normalizedInv);
          setSavedInvForm(normalizedInv);
        }
      } catch (e) {
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      setAlert({ type: 'error', msg: t('contractSettings.logoType') });
      e.target.value = '';
      return;
    }
    if (file.size > 1024 * 1024) {
      setAlert({ type: 'error', msg: t('contractSettings.logoTooBig') });
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, company_logo: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const removeLogo = () => setForm(f => ({ ...f, company_logo: '' }));

  // Pigułki emaila faktury — wstawiają zmienną jako PIGUŁKĘ w aktywnym polu (w miejscu kursora).
  const activeEmailEditor = useRef(null);
  const insertInvoiceVar = (name) => {
    const ed = activeEmailEditor.current;
    if (!ed) return;
    ed.chain().focus().insertContent({ type: 'variablePill', attrs: { name } }).run();
  };
  const setInv = (field) => (e) => setInvForm(f => ({ ...f, [field]: e.target.value }));
  const setInvNum = (field) => (e) => setInvForm(f => ({ ...f, [field]: e.target.value }));
  const setInvBool = (field) => (e) => setInvForm(f => ({ ...f, [field]: e.target.checked }));

  // ── Zapis ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (loadFailed) return;
    // Wysokość logo poza zakresem: komunikat przy polu i u góry, nic nie idzie na serwer.
    // W przerwie danych firmy i tak nie zapisujemy, więc ich błąd nie wstrzymuje ustawień faktur.
    const wysokosc = wysokoscLogoZPola(form.company_logo_height_px);
    if (!wPrzerwie && logoHeightChanged && wysokosc.blad) {
      setLogoHeightTouched(true);
      setAlert({ type: 'error', msg: t('contractSettings.logoHeightError', { min: LOGO_WYSOKOSC.min, max: LOGO_WYSOKOSC.max }) });
      scrollToTop();
      return;
    }
    const bladVat = bladPodstawy(invForm);
    if (bladVat) {
      setAlert({ type: 'error', msg: t(bladVat) });
      scrollToTop();
      return;
    }
    // Puste pole zapisuje się jawnie jako wartość domyślna i tak wraca do pola po zapisie.
    const doZapisu = logoHeightChanged ? { ...form, company_logo_height_px: wysokosc.wartosc } : form;
    setSaving(true);
    setAlert(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      // W przerwie wysyłamy wyłącznie ustawienia faktur: dane firmy zmienione przed włączeniem
      // przerwy (otwarta karta) zostają niezapisane i widoczne jako zmiana, zamiast dawać 403.
      const zmiany = wPrzerwie ? {} : polaDanychFirmy(doZapisu, savedForm);
      if (Object.keys(zmiany).length > 0) {
        const res = await fetch(`${API_BASE}/api/contract-settings`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
          body: JSON.stringify(zmiany),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || t('contractSettings.saveError'));
      }

      const zmianyFaktur = polaUstawienFaktur(invForm, savedInvForm);
      if (Object.keys(zmianyFaktur).length > 0) {
        const invRes = await fetch(`${API_BASE}/api/invoice-settings`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
          body: JSON.stringify(zmianyFaktur),
        });
        if (!invRes.ok) {
          const ed = await invRes.json().catch(() => ({}));
          throw new Error(ed.error || t('contractSettings.saveInvoiceError'));
        }
      }

      if (!wPrzerwie) {
        setForm(doZapisu);
        setSavedForm(doZapisu);
        setLogoHeightTouched(false);
      }
      setSavedInvForm(invForm);
      setAlert({ type: 'success', msg: t('contractSettings.saved') });
      scrollToTop();
    } catch (e) {
      setAlert({ type: 'error', msg: e.message || t('contractSettings.saveGenericError') });
      scrollToTop();
    } finally {
      setSaving(false);
    }
  };

  const handleGoToBuilder = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: t('contractSettings.unsavedTitle'),
        message: t('contractSettings.unsavedBody'),
      });
      if (!confirmed) return;
    }
    const missing = getMissingCompanyFields(form);
    if (missing.length > 0) {
      const ok = await confirm({
        title: t('contractSettings.missingDataTitle'),
        message: t('contractSettings.missingDataBody', { list: missing.map(m => m.label).join(', ') }),
      });
      if (!ok) return;
    }
    navigate('/contract-template');
  };

  if (loading) return (
    <Container>
      <Header>
        <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
          <ArrowLeftIcon />
        </IconButton>
        <HeaderCenter>
          <Title><TitleIcon><BuildingIcon /></TitleIcon>{t('contractSettings.title')}</Title>
        </HeaderCenter>
      </Header>
      <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{t('contractSettings.loading')}</p>
    </Container>
  );

  return (
    <Container>
      <Header>
        <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
          <ArrowLeftIcon />
        </IconButton>
        <HeaderCenter>
          <Title>
            <TitleIcon><BuildingIcon /></TitleIcon>
            {t('contractSettings.title')}
          </Title>
          <Subtitle>
            {t('contractSettings.subtitle')}
          </Subtitle>
        </HeaderCenter>
      </Header>

      {loadFailed && (
        <Alert type="error" role="alert" data-cy="company-settings-load-failed">
          <InfoIcon />
          {`${t('contractSettings.loadError')} ${t('contractSettings.saveBlockedAfterLoadError')}`}
        </Alert>
      )}

      {alert && (
        <Alert type={alert.type}>
          {alert.type === 'success' ? <CheckIcon /> : <InfoIcon />}
          {alert.msg}
        </Alert>
      )}

      {isDirty && (
        <Alert type="info">
          <InfoIcon />
          {t('contractSettings.unsavedBanner')}
        </Alert>
      )}

      {wPrzerwie && (
        <Alert type="info" data-cy="company-settings-pause-note">
          <InfoIcon />
          {powodOdczytu === 'rezygnacja' ? t('rezygnacja.daneFirmy') : t('contractSettings.pauseNote')}
        </Alert>
      )}

      {/* Dane firmy, logo, konto i reprezentant: w przerwie tylko do podglądu (F.24). */}
      <TylkoOdczytWPrzerwie data-cy="company-settings-company-part">
      <Card>
        <CardTitle>{t('contractSettings.companyCard')}</CardTitle>
        <Grid>
          <FormGroup $full>
            <Label>{t('contractSettings.companyName')}</Label>
            <Input value={form.company_name} onChange={set('company_name')} placeholder={t('contractSettings.companyNamePh')} />
          </FormGroup>
          <FormGroup $full>
            <Label>{t('contractSettings.street')}</Label>
            <Input value={form.company_street} onChange={set('company_street')} placeholder={t('contractSettings.streetPh')} maxLength={255} />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.postalCode')}</Label>
            <Input value={form.company_postal_code} onChange={set('company_postal_code')} placeholder="00-001" maxLength={10} />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.city')}</Label>
            <Input value={form.company_city} onChange={set('company_city')} placeholder={t('contractSettings.cityPh')} maxLength={100} />
          </FormGroup>
          <FormGroup>
            <Label>NIP</Label>
            <Input value={form.company_nip} onChange={set('company_nip')} placeholder="123-456-78-90" maxLength={20} />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.regon')}</Label>
            <Input value={form.company_regon} onChange={set('company_regon')} placeholder="123456789" maxLength={20} />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.krs')}</Label>
            <Input value={form.company_krs} onChange={set('company_krs')} placeholder="0000123456" maxLength={20} />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.phone')}</Label>
            <Input value={form.company_phone} onChange={set('company_phone')} placeholder="+48 123 456 789" maxLength={50} />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.email')}</Label>
            <Input type="email" value={form.company_email} onChange={set('company_email')} placeholder={t('contractSettings.emailPh')} />
          </FormGroup>
        </Grid>
      </Card>

      {/* Logo firmy */}
      <Card>
        <CardTitle>{t('contractSettings.logoCard')}</CardTitle>
        <p style={{ margin: '0 0 14px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {t('contractSettings.logoHint')}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          {form.company_logo ? (
            <img
              src={form.company_logo}
              alt={t('contractSettings.logoCard')}
              /* biały podkład — podgląd tego, jak logo wygląda na białej stronie generowanej umowy */
              style={{ maxHeight: '70px', maxWidth: '200px', objectFit: 'contain', border: '1px solid var(--input-border)', borderRadius: '8px', padding: '8px', background: '#fff' }}
            />
          ) : (
            <div style={{ height: '70px', width: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--input-border)', borderRadius: '8px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {t('contractSettings.noLogo')}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-start' }}>
            <label style={{ display: 'inline-block', padding: '9px 16px', background: 'var(--primary)', color: 'var(--primary-contrast)', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
              {form.company_logo ? t('contractSettings.changeLogo') : t('contractSettings.pickLogo')}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handleLogoFile}
                style={{ display: 'none' }}
              />
            </label>
            {form.company_logo && (
              <button type="button" onClick={removeLogo} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}>
                {t('contractSettings.removeLogo')}
              </button>
            )}
          </div>
        </div>
        {form.company_logo && (
          <Grid style={{ marginTop: '16px' }}>
            <FormGroup>
              <Label>{t('contractSettings.logoAlign')}</Label>
              <SimpleSelect
                style={{ width: '100%' }}
                value={form.company_logo_align}
                onChange={(val) => setForm(f => ({ ...f, company_logo_align: val }))}
                options={[
                  { value: 'left', label: t('contractSettings.alignLeft') },
                  { value: 'center', label: t('contractSettings.alignCenter') },
                  { value: 'right', label: t('contractSettings.alignRight') },
                ]}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="company-logo-height">{t('contractSettings.logoHeight')}</Label>
              {/* Surowy tekst pola: wyczyszczenie zostawia puste pole (= 70 px), a nie wpisuje 70. */}
              <Input
                id="company-logo-height"
                type="number"
                inputMode="numeric"
                min={LOGO_WYSOKOSC.min}
                max={LOGO_WYSOKOSC.max}
                step={1}
                placeholder={String(LOGO_WYSOKOSC.domyslna)}
                value={form.company_logo_height_px ?? ''}
                aria-invalid={logoHeightTouched && logoHeightError ? 'true' : undefined}
                aria-describedby="company-logo-height-hint"
                onChange={(e) => setForm(f => ({ ...f, company_logo_height_px: e.target.value }))}
                onBlur={() => setLogoHeightTouched(true)}
              />
              {logoHeightTouched && logoHeightError ? (
                <FieldError role="alert" data-cy="logo-height-error">
                  {t('contractSettings.logoHeightError', { min: LOGO_WYSOKOSC.min, max: LOGO_WYSOKOSC.max })}
                </FieldError>
              ) : null}
              <FieldHint id="company-logo-height-hint">
                {t('contractSettings.logoHeightHint', { min: LOGO_WYSOKOSC.min, max: LOGO_WYSOKOSC.max, def: LOGO_WYSOKOSC.domyslna })}
              </FieldHint>
            </FormGroup>
          </Grid>
        )}
      </Card>

      {/* Dane bankowe */}
      <Card>
        <CardTitle>{t('contractSettings.bankCard')}</CardTitle>
        <Grid>
          <FormGroup>
            <Label>{t('contractSettings.iban')}</Label>
            <Input value={form.company_bank_account} onChange={set('company_bank_account')} placeholder={t('contractSettings.ibanPh')} maxLength={34} />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.bankName')}</Label>
            <Input value={form.company_bank_name} onChange={set('company_bank_name')} placeholder={t('contractSettings.bankNamePh')} />
          </FormGroup>
        </Grid>
      </Card>

      {/* Reprezentant */}
      <Card>
        <CardTitle>{t('contractSettings.signerCard')}</CardTitle>
        <Grid>
          <FormGroup>
            <Label>{t('contractSettings.signerName')}</Label>
            <Input value={form.representative_name} onChange={set('representative_name')} placeholder={t('contractSettings.signerNamePh')} />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.signerTitle')}</Label>
            <Input value={form.representative_title} onChange={set('representative_title')} placeholder={t('contractSettings.signerTitlePh')} maxLength={100} />
          </FormGroup>
        </Grid>
      </Card>
      </TylkoOdczytWPrzerwie>

      {/* Ustawienia faktur: działają także w przerwie (F.24). */}
      <Card data-cy="company-settings-invoice-card">
        <CardTitle>{t('contractSettings.invoiceCard')}</CardTitle>
        <Grid>
          <FormGroup>
            <Label>{t('contractSettings.vatStatus')}</Label>
            <SimpleSelect
              style={{ width: '100%' }}
              data-cy="vat-status"
              aria-label={t('contractSettings.vatStatus')}
              value={invForm.vat_status}
              onChange={(val) => setInvForm(f => ({ ...f, vat_status: val }))}
              options={[
                { value: 'active', label: t('contractSettings.vatStatusActive') },
                { value: 'exempt', label: t('contractSettings.vatStatusExempt') },
              ]}
            />
          </FormGroup>
          {invForm.vat_status !== 'exempt' && (
            <FormGroup>
              <Label>{t('contractSettings.vatRate')}</Label>
              <SimpleSelect
                style={{ width: '100%' }}
                data-cy="default-vat-rate"
                aria-label={t('contractSettings.vatRate')}
                value={invForm.default_vat_rate}
                onChange={(val) => setInvForm(f => ({ ...f, default_vat_rate: val }))}
                options={[
                  ...STAWKI_DOMYSLNE.map((v) => ({
                    value: v,
                    label: v === '0' ? t('contractSettings.vatRateZero') : `${v}%`,
                  })),
                  // Stawka zapisana przed F.14 spoza listy: widoczna, żeby nikt nie myślał, że to 23%.
                  ...(STAWKI_DOMYSLNE.includes(invForm.default_vat_rate) ? [] : [{
                    value: invForm.default_vat_rate,
                    label: t('contractSettings.vatRateLegacy', { rate: String(invForm.default_vat_rate).replace('.', ',') }),
                  }]),
                ]}
              />
            </FormGroup>
          )}
          <FormGroup>
            <Label>{t('contractSettings.payMethod')}</Label>
            <SimpleSelect
              style={{ width: '100%' }}
              value={invForm.default_payment_method}
              onChange={(val) => setInvForm(f => ({ ...f, default_payment_method: val }))}
              options={Object.entries(PAYMENT_METHOD_KEYS).map(([k, key]) => ({ value: k, label: t(key) }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t('contractSettings.payTerm')}</Label>
            <Input
              type="number"
              min="0"
              max="365"
              value={invForm.default_payment_term_days}
              onChange={setInvNum('default_payment_term_days')}
              placeholder="7"
            />
          </FormGroup>
        </Grid>
        {invForm.vat_status !== 'exempt' && invForm.default_vat_rate === '0' && (
          <FieldHint data-cy="vat-zero-hint">{t('contractSettings.vatZeroHint')}</FieldHint>
        )}
        {invForm.vat_status === 'exempt' && (
          <FormGroup $full style={{ marginTop: 16 }}>
            <Label htmlFor="vat-exemption-basis">{t('contractSettings.vatBasis')}</Label>
            <Input
              id="vat-exemption-basis"
              data-cy="vat-exemption-basis"
              maxLength={256}
              value={invForm.vat_exemption_basis}
              onChange={setInv('vat_exemption_basis')}
              placeholder={t('contractSettings.vatBasisPh')}
            />
            <TagList>
              {PODSTAWY_ZWOLNIENIA.map((p) => (
                <HintChip
                  key={p.wartosc}
                  type="button"
                  data-cy="vat-basis-hint"
                  onClick={() => setInvForm(f => ({ ...f, vat_exemption_basis: p.wartosc }))}
                >
                  {t(p.etykieta)}
                </HintChip>
              ))}
            </TagList>
            <FieldHint>{t('contractSettings.vatExemptHint')}</FieldHint>
          </FormGroup>
        )}
        <div style={{ marginTop: 20 }}>
          <Label style={{ display: 'block', marginBottom: 10 }}>{t('contractSettings.prefixes')}</Label>
          <Grid>
            <FormGroup>
              <Label style={{ fontWeight: 500, textTransform: 'none' }}>{t('contractSettings.prefixVat')}</Label>
              <Input value={invForm.invoice_prefix_vat} onChange={setInv('invoice_prefix_vat')} placeholder="FV" maxLength={10} />
            </FormGroup>
            <FormGroup>
              <Label style={{ fontWeight: 500, textTransform: 'none' }}>{t('contractSettings.prefixProforma')}</Label>
              <Input value={invForm.invoice_prefix_proforma} onChange={setInv('invoice_prefix_proforma')} placeholder="PRO" maxLength={10} />
            </FormGroup>
            <FormGroup>
              <Label style={{ fontWeight: 500, textTransform: 'none' }}>{t('contractSettings.prefixSimplified')}</Label>
              <Input value={invForm.invoice_prefix_simplified} onChange={setInv('invoice_prefix_simplified')} placeholder="PAR" maxLength={10} />
            </FormGroup>
            <FormGroup>
              <Label style={{ fontWeight: 500, textTransform: 'none' }}>{t('contractSettings.prefixCorrection')}</Label>
              <Input value={invForm.invoice_prefix_correction} onChange={setInv('invoice_prefix_correction')} placeholder="KOR" maxLength={10} />
            </FormGroup>
          </Grid>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            {t('contractSettings.numberFormat')} <code style={{ background: 'var(--input-border)', padding: '1px 6px', borderRadius: 4 }}>{invForm.invoice_prefix_vat || 'FV'}/2026/01/001</code>
          </p>
        </div>

        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--input-border)' }}>
          <Toggle checked={!!invForm.invoice_auto_email}>
            <input
              type="checkbox"
              checked={!!invForm.invoice_auto_email}
              onChange={setInvBool('invoice_auto_email')}
            />
            <span className="track" />
            {t('contractSettings.autoEmail')}
          </Toggle>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, marginBottom: 16 }}>
            {t('contractSettings.autoEmailHint')}
          </p>

          {invForm.invoice_auto_email && (
            <Grid>
              <FormGroup $full>
                <Label>{t('contractSettings.emailSubject')}</Label>
                <EmailPillField
                  value={invForm.invoice_email_subject || ''}
                  labelFor={invoiceLabelFor}
                  singleLine
                  onChange={(text) => setInvForm(f => ({ ...f, invoice_email_subject: text }))}
                  onFocusEditor={(ed) => { activeEmailEditor.current = ed; }}
                />
              </FormGroup>
              <FormGroup $full>
                <Label>{t('contractSettings.emailBody')}</Label>
                <EmailPillField
                  value={invForm.invoice_email_body || ''}
                  labelFor={invoiceLabelFor}
                  onChange={(text) => setInvForm(f => ({ ...f, invoice_email_body: text }))}
                  onFocusEditor={(ed) => { activeEmailEditor.current = ed; }}
                />
              </FormGroup>
            </Grid>
          )}

          <p style={{ margin: '12px 0 6px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            {t('contractSettings.insertVarHint')}
          </p>
          <TagList>
            {Object.keys(INVOICE_VAR_KEYS).map((name) => (
              <Tag
                key={name}
                onClick={() => insertInvoiceVar(name)}
                title={t('contractSettings.insertVarTitle')}
                style={{ cursor: 'pointer', background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none', fontWeight: 600, padding: '6px 12px' }}
              >
                {invoiceLabelFor(name)}
              </Tag>
            ))}
          </TagList>
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <PrimaryButton onClick={handleGoToBuilder}>
          {t('contractSettings.goToBuilder')}
        </PrimaryButton>
        <PrimaryButton onClick={handleSave} disabled={saving || !(wPrzerwie ? invDirty : isDirty) || loadFailed} data-cy="company-settings-save">
          <SaveIcon />
          {saving ? t('contractSettings.saving') : t('contractSettings.save')}
        </PrimaryButton>
      </div>
    </Container>
  );
}
